import { resolveComboLinePrices } from '@tcr-erp/shared';
import type { OrderItem } from '../types';

export interface PosComboComponent {
  variant_id: string;
  sku: string;
  product_name: string;
  qty: number;
  retail_price: number;
  /**
   * OPTIONAL CHỈ TRONG GIAI ĐOẠN CHUYỂN TIẾP — XOÁ DẤU `?` Ở PR DỌN DẸP (spec
   * §8.2 bước 5). Vắng ⇒ record chưa backfill, `resolveComboLinePrices` rơi về
   * phân bổ theo `combo_price`.
   */
  price_in_combo?: number;
}

export interface PosComboDefinition {
  _id: string;
  combo_sku: string;
  combo_name: string;
  combo_price: number;
  component_items: PosComboComponent[];
}

export interface ComboSuggestionUse {
  itemId: string;
  qty: number;
}

export interface ComboSuggestion {
  combo_id: string;
  combo_sku: string;
  combo_name: string;
  /** Số combo gộp được. */
  unit_count: number;
  /** Dòng nào bị dùng bao nhiêu đơn vị. */
  uses: ComboSuggestionUse[];
  /** Giá lẻ của phần bị gộp. */
  retail_total: number;
  /** Giá thực thu của phần bị gộp. */
  combo_total: number;
  saving: number;
}

/** Một dòng giỏ khiến combo bị loại vì gộp vào sẽ làm dòng đó ĐẮT lên. */
export interface ComboBlockedLine {
  itemId: string;
  productName: string;
  /** Giá dòng đang bán trong giỏ. */
  currentPrice: number;
  /** Giá phân bổ của dòng nếu gộp combo — luôn > `currentPrice`. */
  comboPrice: number;
}

/**
 * Combo KHỚP ĐỦ hàng trong giỏ nhưng bị chặn bởi luật "không làm tăng giá dòng
 * nào". Không có thứ này thì màn hình im lặng hoàn toàn: thu ngân thấy đủ hàng
 * của một combo đang chạy mà không có gợi ý nào, và không có cách nào biết vì
 * sao (đã xảy ra thật — một thành phần bị để giá 0đ).
 */
export interface ComboPriceBlock {
  combo_id: string;
  combo_sku: string;
  combo_name: string;
  unit_count: number;
  /** Mức tiết kiệm sẽ quảng cáo NẾU áp được — để xếp hạng cái đáng nói nhất. */
  saving: number;
  /** Các dòng bị đẩy giá lên (đã gộp theo dòng giỏ). */
  lines: ComboBlockedLine[];
}

/** Dòng đủ điều kiện gộp combo. Xem spec §3.4. */
function isEligible(item: OrderItem): boolean {
  return (
    item.trackingMode === 'quantity' &&
    !item.comboGroupId &&
    !item.is_gift &&
    !item.flash_sale_id &&
    !item.hasPriceOverride
  );
}

/**
 * `resolveComboLinePrices` throw khi định nghĩa combo không hợp lệ (thành phần
 * rỗng, qty không phải số nguyên dương, hoặc tổng giá lẻ < 0). Guard này kiểm
 * tra mỗi component có qty hợp lệ & retail_price >= 0, phòng khi BE config sai.
 * Matcher chạy trong `useMemo` lúc render POS: throw ở đây sẽ crash cả màn hình.
 */
function canPriceCombo(def: PosComboDefinition): boolean {
  if (def.component_items.length === 0) return false;
  return def.component_items.every(
    (c) => Number.isInteger(c.qty) && c.qty >= 1 && c.retail_price >= 0,
  );
}

/**
 * Tìm tổ hợp combo tiết kiệm nhất cho giỏ hàng. Tham lam theo mức lợi mỗi combo:
 * combo lợi nhiều đi trước và ăn hết phần nó gộp được, combo sau chỉ xét phần
 * còn lại. Hàm thuần — không đụng React, không gọi mạng.
 */
export function matchCombos(
  items: OrderItem[],
  definitions: PosComboDefinition[],
  opts: { isB2B: boolean },
): ComboSuggestion[] {
  return matchComboState(items, definitions, opts).suggestions;
}

/**
 * Như `matchCombos` nhưng trả THÊM danh sách combo khớp đủ hàng mà bị luật
 * "không làm tăng giá dòng nào" loại (`blocked`) — để giao diện nói ra lý do
 * thay vì im lặng. Tách làm hàm riêng thay vì đổi kiểu trả của `matchCombos`:
 * hàm đó đang được dùng ở nhiều nơi và ý nghĩa "danh sách gợi ý" của nó không
 * đổi.
 */
export function matchComboState(
  items: OrderItem[],
  definitions: PosComboDefinition[],
  opts: { isB2B: boolean },
): { suggestions: ComboSuggestion[]; blocked: ComboPriceBlock[] } {
  if (opts.isB2B) return { suggestions: [], blocked: [] };

  // Pool tồn khả dụng theo variant, giữ thứ tự dòng để `uses` ổn định.
  const pool = new Map<string, Array<{ itemId: string; left: number }>>();
  for (const item of items) {
    if (!isEligible(item)) continue;
    const bucket = pool.get(item.variantId);
    if (bucket) bucket.push({ itemId: item.id, left: item.quantity });
    else pool.set(item.variantId, [{ itemId: item.id, left: item.quantity }]);
  }

  const ranked = definitions
    .filter((def) => canPriceCombo(def))
    .map((def) => ({
      def,
      // Dùng đúng hàm giá dùng chung — không tự cộng dồn retail_price × qty,
      // vì resolveComboLinePrices (đọc price_in_combo nếu có, không thì rơi về
      // priceComboComponents làm tròn từng dòng) khiến saving_total thật có
      // thể khác con số cộng dồn thô tới ceil(Σqty / 2) đồng.
      savingPerUnit: resolveComboLinePrices(def.component_items, def.combo_price, 1).saving_total,
    }))
    .filter((entry) => entry.savingPerUnit > 0)
    .sort((a, b) => b.savingPerUnit - a.savingPerUnit);

  const suggestions: ComboSuggestion[] = [];
  const blocked: ComboPriceBlock[] = [];
  /** Giá hiện tại của từng dòng trong giỏ — để chặn gợi ý làm TĂNG giá dòng. */
  const priceByItemId = new Map(items.map((item) => [item.id, item.price]));
  const nameByItemId = new Map(items.map((item) => [item.id, item.variantName]));

  for (const { def } of ranked) {
    const available = (variantId: string): number =>
      (pool.get(variantId) ?? []).reduce((acc, slot) => acc + slot.left, 0);

    const unitCount = def.component_items.reduce(
      (acc, component) =>
        Math.min(acc, Math.floor(available(component.variant_id) / component.qty)),
      Number.POSITIVE_INFINITY,
    );
    if (!Number.isFinite(unitCount) || unitCount < 1) continue;

    const pricing = resolveComboLinePrices(def.component_items, def.combo_price, unitCount);

    // Dự tính sẽ rút bao nhiêu từ dòng nào — CHƯA trừ khỏi pool. Phải kiểm tra
    // xong mới được commit: một combo bị loại ở bước dưới không được ăn mất
    // hàng của các combo xếp sau nó.
    type Slot = { itemId: string; left: number };
    const planned: Array<{ slot: Slot; take: number; variantId: string }> = [];
    const reserved = new Map<Slot, number>();
    for (const component of def.component_items) {
      let need = component.qty * unitCount;
      for (const slot of pool.get(component.variant_id) ?? []) {
        if (need === 0) break;
        const take = Math.min(slot.left - (reserved.get(slot) ?? 0), need);
        if (take <= 0) continue;
        need -= take;
        reserved.set(slot, (reserved.get(slot) ?? 0) + take);
        planned.push({ slot, take, variantId: component.variant_id });
      }
    }

    // `component_items[].retail_price` là ảnh chụp lúc CẤU HÌNH combo và không
    // bao giờ được đồng bộ lại. Nếu giá lẻ thật của một thành phần đã GIẢM
    // xuống dưới giá phân bổ, "áp dụng" sẽ KÉO GIÁ DÒNG ĐÓ LÊN trong khi banner
    // vẫn quảng cáo một khoản tiết kiệm — mức tiết kiệm đó tính trên giá lẻ cũ,
    // không phải giá khách đang thấy trong giỏ. Chỉ gợi ý khi mọi dòng bị ảnh
    // hưởng đều được HẠ giá hoặc GIỮ NGUYÊN.
    const raisedLines = new Map<string, ComboBlockedLine>();
    for (const plan of planned) {
      const allocated = pricing.lines.find((line) => line.variant_id === plan.variantId);
      const currentPrice = priceByItemId.get(plan.slot.itemId);
      if (!allocated || currentPrice === undefined) continue;
      if (allocated.unit_price <= currentPrice) continue;
      // Gộp theo dòng giỏ: một dòng chỉ nói MỘT lần dù bị nhiều lát cắt chạm tới.
      raisedLines.set(plan.slot.itemId, {
        itemId: plan.slot.itemId,
        productName: nameByItemId.get(plan.slot.itemId) ?? '',
        currentPrice,
        comboPrice: allocated.unit_price,
      });
    }
    if (raisedLines.size > 0) {
      // Combo bị loại KHÔNG trừ pool (xem comment ở `planned`) — chỉ ghi lại lý
      // do để giao diện nói ra, rồi nhường hàng cho combo xếp sau.
      blocked.push({
        combo_id: def._id,
        combo_sku: def.combo_sku,
        combo_name: def.combo_name,
        unit_count: unitCount,
        saving: pricing.saving_total,
        lines: [...raisedLines.values()],
      });
      continue;
    }

    const uses: ComboSuggestionUse[] = [];
    for (const plan of planned) {
      plan.slot.left -= plan.take;
      const existing = uses.find((use) => use.itemId === plan.slot.itemId);
      if (existing) existing.qty += plan.take;
      else uses.push({ itemId: plan.slot.itemId, qty: plan.take });
    }
    suggestions.push({
      combo_id: def._id,
      combo_sku: def.combo_sku,
      combo_name: def.combo_name,
      unit_count: unitCount,
      uses,
      retail_total: pricing.retail_total,
      combo_total: pricing.effective_total,
      saving: pricing.saving_total,
    });
  }

  return { suggestions, blocked };
}
